import { useCallback } from 'react'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { usePurchaseContentCreditsModal } from 'hooks/usePurchaseContentCreditsModal'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'

export const useCreditsGate = <TArgs extends unknown[]>(
  charge: number,
  action?: (...args: TArgs) => void | Promise<void>,
) => {
  const { creditsRemaining, canAutoReload } = useCreditUsage()
  const openPurchaseModal = usePurchaseContentCreditsModal()
  const workspace = useCurrentWorkspace()
  const {
    tierUsageStore: { autopilotAddOns },
  } = useRootStore()

  const autopilotContentCreditsPrice = autopilotAddOns.find(
    ({ key }) => key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )?.prices[0]
  const needsCredits = charge > creditsRemaining
  const autoReloadBudgetReached = Boolean(workspace?.auto_reload_credits && !canAutoReload)

  return useCallback(
    async (...args: TArgs) => {
      if (autoReloadBudgetReached && autopilotContentCreditsPrice) {
        return openPurchaseModal(
          {
            price: autopilotContentCreditsPrice,
            defaultBatchCount: Math.max(1, Math.ceil(Math.max(charge, 100) / 100)),
            onSuccess: () => action?.(...args),
          },
          { newStack: true },
        )
      }

      if (needsCredits && autopilotContentCreditsPrice) {
        return openPurchaseModal(
          {
            price: autopilotContentCreditsPrice,
            defaultBatchCount: Math.ceil((charge - creditsRemaining) / 100),
            onSuccess: () => action?.(...args),
          },
          { newStack: true },
        )
      }

      return action?.(...args)
    },
    [
      autoReloadBudgetReached,
      needsCredits,
      autopilotContentCreditsPrice,
      charge,
      creditsRemaining,
      openPurchaseModal,
      action,
    ],
  )
}
